// frontend/src/components/product/RelatedProducts.js - Fixed with perPack support

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicInstance } from '../../utils/axiosConfig';
import { IndianRupee, ShoppingBag } from 'lucide-react';

const RelatedProducts = ({ currentProduct }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRelatedProducts = async () => {
            try {
                if (!currentProduct?.category) {
                    setLoading(false);
                    setProducts([]);
                    return;
                }

                // Get the parent category for better related product matching
                let categoryQuery = currentProduct.category;
                if (currentProduct.category.includes('Mutton')) {
                    categoryQuery = 'Mutton -';
                } else if (currentProduct.category.includes('Chicken')) {
                    categoryQuery = 'Chicken -';
                } else if (currentProduct.category.includes('Egg')) {
                    categoryQuery = 'Egg';
                }

                const { data } = await publicInstance.get(`/products?category=${encodeURIComponent(categoryQuery)}`);
                
                if (!data.products || data.products.length === 0) {
                    setLoading(false);
                    return;
                }

                // Filter out current product and get only 4 related products
                // Only include visible products
                const relatedProducts = data.products
                    .filter(p => p._id !== currentProduct._id && p.isVisible)
                    .sort(() => 0.5 - Math.random())
                    .slice(0, 4);

                setProducts(relatedProducts);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchRelatedProducts();
    }, [currentProduct]);

    const getDisplayPrice = (product) => {
        if (!product) return '0.00';
        
        if (product.priceType === 'perKg') {
            return product.price ? product.price.toFixed(2) : '0.00';
        } else if (product.priceType === 'perDozen') {
            return product.dozenPrice ? product.dozenPrice.toFixed(2) : '0.00';
        } else if (product.priceType === 'perPack') {
            return product.packPrice ? product.packPrice.toFixed(2) : '0.00';
        }
        
        if (product.weightOptions && product.weightOptions.length > 0) {
            // Only consider visible weight options
            const visibleOptions = product.weightOptions.filter(opt => opt.visible);
            if (visibleOptions.length === 0) return '0.00';
            
            const prices = visibleOptions.map(opt => opt.price);
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            
            if (minPrice === maxPrice) {
                return minPrice.toFixed(2);
            }
            return `${minPrice.toFixed(2)} - ${maxPrice.toFixed(2)}`;
        }
        
        return '0.00';
    };

    const calculateDiscountedPrice = (product) => {
        let originalPrice;
        
        if (product.priceType === 'perKg') {
            originalPrice = product.price;
        } else if (product.priceType === 'perDozen') {
            originalPrice = product.dozenPrice;
        } else if (product.priceType === 'perPack') {
            originalPrice = product.packPrice;
        } else {
            // For fixed price, look at visible weight options
            const visibleOptions = product.weightOptions?.filter(opt => opt.visible) || [];
            originalPrice = visibleOptions.length > 0 ? visibleOptions[0].price : 0;
        }
            
        if (!originalPrice || !product.discount?.active) return originalPrice;
        
        if (product.discount.type === 'percentage') {
            return originalPrice - (originalPrice * product.discount.value / 100);
        }
        
        return originalPrice - product.discount.value;
    };

    if (error || loading || products.length === 0) {
        return null;
    }

    const ProductCard = ({ product }) => (
        <div className="transform transition-transform hover:-translate-y-1">
            <Link to={`/product/${product._id}`} className="block relative">
                <div className="relative h-40 w-full">
                    <div className="w-full h-full overflow-hidden">
                        {product.images && product.images[0] ? (
                            <img
                                src={product.images[0].image}
                                alt={product.name}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                                }}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <ShoppingBag className="w-10 h-10 text-gray-400" />
                            </div>
                        )}
                    </div>
                    {product.discount?.active && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                            {product.discount.type === 'percentage'
                                ? `${product.discount.value}% OFF`
                                : `₹${product.discount.value} OFF`
                            }
                        </div>
                    )}
                </div>
            </Link>
            <div className="pt-2 flex flex-col items-center text-center">
                <Link to={`/product/${product._id}`} className="block w-full">
                    <h3 className="font-medium text-xs text-gray-800 line-clamp-1 mb-1 hover:text-[#dc2626] transition-colors">
                        {product.name || 'Unnamed Product'}
                    </h3>
                </Link>
                
                <div className="flex flex-col items-center mb-2">
                    {product.discount?.active ? (
                        <>
                            <span className="text-gray-500 line-through text-xs">
                                ₹{getDisplayPrice(product)}
                            </span>
                            <div className="text-[#dc2626] font-bold text-xs">
                                ₹{calculateDiscountedPrice(product)?.toFixed(2)}
                                {product.priceType === 'perKg' && <span className="text-xs font-normal ml-1">/kg</span>}
                                {product.priceType === 'perDozen' && <span className="text-xs font-normal ml-1">/dozen</span>}
                                {product.priceType === 'perPack' && <span className="text-xs font-normal ml-1">/pack</span>}
                            </div>
                        </>
                    ) : (
                        <div className="text-[#dc2626] font-bold text-xs">
                            ₹{getDisplayPrice(product)}
                            {product.priceType === 'perKg' && <span className="text-xs font-normal ml-1">/kg</span>}
                            {product.priceType === 'perDozen' && <span className="text-xs font-normal ml-1">/dozen</span>}
                            {product.priceType === 'perPack' && <span className="text-xs font-normal ml-1">/pack</span>}
                        </div>
                    )}
                </div>
                <Link
                    to={`/product/${product._id}`}
                    className="inline-block w-3/4 text-center bg-[#dc2626] text-white py-1 rounded-md text-xs hover:bg-[#dc2626]/90 transition-colors mx-auto"
                >
                    View Details
                </Link>
            </div>
        </div>
    );

    return (
        <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
                Related Products
            </h2>
            {/* Desktop View */}
            <div className="hidden md:grid md:grid-cols-4 gap-4">
                {products.map(product => (
                    <ProductCard key={product._id} product={product} />
                ))}
            </div>
            {/* Mobile View with Animation */}
            <div className="md:hidden relative overflow-hidden">
                <div className="flex animate-scroll space-x-4 py-2">
                    {[...products, ...products].map((product, index) => (
                        <div
                            key={`${product._id}-${index}`}
                            className="flex-shrink-0 w-40"
                        >
                            <ProductCard product={product} />
                        </div>
                    ))}
                </div>
            </div>
            <style jsx>{`
                @keyframes scroll {
                    0% {
                        transform: translateX(0);
                    }
                    100% {
                        transform: translateX(-50%);
                    }
                }
                .animate-scroll {animation: scroll 30s linear infinite;
                }
                .animate-scroll:hover {
                    animation-play-state: paused;
                }
            `}</style>
        </div>
    );
};

export default RelatedProducts;